import { TextInput } from '@gettactic/components/src/form/TextInput';
import { capitalize } from '@gettactic/helpers/src/strings/capitalize';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Underline
} from 'lucide-react';
import React, { useMemo } from 'react';
import {
  ShapeTextAlignment,
  SpotShape,
  SpotShapeTypeToLabel,
  TextShape
} from '../../../../../../types';
import { ColorPickerInput } from '../../../../../ui/ColorPickerInput';
import { ToggleGroup, ToggleGroupItem } from '../../../../../ui/toggle-group';
import { useMapEditor } from '../../../../contexts/MapEditorContext';
import {
  DEFAULT_NODE_LABEL_ALIGNMENT,
  DEFAULT_NODE_RECT_COLOR
} from '../../../../defines';
import { PanelSectionWrapper } from '../PanelSectionWrapper';
import { ShapeSectionOptions } from '../ShapePanel';
import { useCustomParkingVerbiageOnMapBuilder as useCustomParkingVerbiage } from '../../../../../../hooks/useCustomParkingVerbiageOnMapBuilder';

export const CommonTextPanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape } = useMapEditor();
  const shape = shapes[0];
  const textShape = shape as TextShape;

  const isNodeType = shape.type === 'spot';

  const handleChangeShapeText = (value: string) => {
    if (shape.type === 'spot') {
      shapes.forEach((shape) => {
        updateShape(shape.id, {
          label: value
        });
      });
      return;
    }
    shapes.forEach((shape) => {
      updateShape(shape.id, { text: value });
    });
  };

  const handleChangeShapeName = (name: string) => {
    if (shape.type === 'spot') {
      shapes.forEach((shape) => {
        updateShape(shape.id, {
          name
        });
      });
      return;
    }
    shapes.forEach((shape) => {
      updateShape(shape.id, { name });
    });
  };

  const handleChangeShapeTextColor = (value: string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { textColor: value });
    });
  };

  const handleSetBold = (value: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isBold: value });
    });
  };

  const handleSetUnderline = (value: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isUnderline: value });
    });
  };

  const handleSetItalic = (value: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isItalic: value });
    });
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { fontSize: Number(e.target.value) });
    });
  };

  const handleFontFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { fontFamily: e.target.value });
    });
  };

  const handleLabelTextAlignChange = (textAlign: ShapeTextAlignment) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { textAlign });
    });
  };

  const shapeWithText = shape as TextShape;

  const toggleGroupValue = React.useMemo(() => {
    const value = [];
    if (shapeWithText.isBold) {
      value.push('bold');
    }
    if (shapeWithText.isUnderline) {
      value.push('underline');
    }
    if (shapeWithText.isItalic) {
      value.push('italic');
    }
    return value;
  }, [shapeWithText.isBold, shapeWithText.isUnderline, shapeWithText.isItalic]);

  const spotShape = shape as SpotShape;
  const text = spotShape.type === 'spot' ? spotShape.label : shapeWithText.text;
  const name = spotShape.type === 'spot' ? spotShape.name : '';

  const textAlignment =
    shape.type === 'spot'
      ? shapeWithText.textAlign || DEFAULT_NODE_LABEL_ALIGNMENT
      : shapeWithText.textAlign;

  const spotAppearance =
    (shape as SpotShape).spotAppearance || (shape as SpotShape).spotType;

  const customParkingVerbiage = useCustomParkingVerbiage();
  const spotTitle = useMemo(() => {
    if (shape.type !== 'spot') return '';
    
    // @ts-ignore <'parking_space' is not added to SpotType yet>
    const spotIsParking = spotAppearance === 'parking_lot' || spotAppearance === 'parking_space';

    let title = capitalize(SpotShapeTypeToLabel[spotAppearance]);
    if(spotIsParking && customParkingVerbiage !== null) {
      title = `${capitalize(customParkingVerbiage)} Group`;
    }
    return title;
  }, [shape.type, customParkingVerbiage, shape]);

  const textField = (
    <div className="flex flex-col gap-1">
      <div className="text-sm font-medium">
        {isNodeType
          ? `${spotTitle} Label`
          : 'Text'}
      </div>
      <textarea
        className={'w-full h-10 rounded-md border border-gray-200 p-2'}
        value={text || ''}
        onChange={(e) => handleChangeShapeText(e.target.value)}
      />
    </div>
  );

  return (
    <PanelSectionWrapper title={isNodeType ? '' : 'Text'}>
      {isNodeType ? (
        <div className="flex gap-2 w-full">
          <div className={'w-60'}>{textField}</div>
          <div className={'w-full'}>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium">
                {capitalize(spotTitle)} Name
              </div>
              <TextInput
                value={name || ''}
                onChange={(e) => handleChangeShapeName(e.target.value)}
              />
            </div>
          </div>
        </div>
      ) : undefined}
      <div className={'flex gap-2'}>
        {!isNodeType ? textField : undefined}
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium">
            {isNodeType
              ? `${capitalize(spotTitle)} Label color`
              : 'Text color'}
          </div>
          <ColorPickerInput
            value={textShape.textColor || DEFAULT_NODE_RECT_COLOR}
            onChange={handleChangeShapeTextColor}
          />
        </div>
      </div>
      {/*<div className="flex flex-col gap-1">
        <div className="text-sm font-medium">Font Family</div>
        <select
          value={(shape as TextShape).fontFamily || DEFAULT_FONT_FAMILY}
          onChange={handleFontFamilyChange}
          className="w-full rounded-md border border-gray-200 p-2"
        >
          {[
            'Quicksand',
            'Arial',
            'Times New Roman',
            'Helvetica',
            'Courier New',
            'Georgia',
            'Verdana',
            'Impact'
          ].map((font) => (
            <option key={font} value={font}>
              {font}
            </option>
          ))}
        </select>
      </div>*/}
      <div className={'flex gap-2 items-center'}>
        <div className={'flex flex-col gap-1'}>
          <ToggleGroup
            type={'multiple' as never}
            value={toggleGroupValue as never}
            onValueChange={
              ((value: string[]) => {
                handleSetBold(value.includes('bold'));
                handleSetUnderline(value.includes('underline'));
                handleSetItalic(value.includes('italic'));
              }) as never
            }
          >
            <ToggleGroupItem value="bold" aria-label="Top">
              <Bold className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="underline" aria-label="Top">
              <Underline className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="italic" aria-label="Top">
              <Italic className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div>
          <ToggleGroup
            type="single"
            value={textAlignment}
            onValueChange={handleLabelTextAlignChange}
          >
            <ToggleGroupItem value="left" aria-label="Top">
              <AlignLeft className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="center" aria-label="Top">
              <AlignCenter className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="right" aria-label="Top">
              <AlignRight className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="w-full">
          <select
            value={(shape as TextShape).fontSize || 20}
            onChange={handleFontSizeChange}
            className="w-full rounded-md border border-gray-200 p-2"
          >
            {[12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72].map(
              (size) => (
                <option key={size} value={size}>
                  {size}px
                </option>
              )
            )}
          </select>
        </div>
      </div>
    </PanelSectionWrapper>
  );
};
